import { QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { AuthProvider } from '@/auth/AuthProvider';
import { useAuth } from '@/auth/auth-context';
import { CurrentMemberContext } from '@/auth/member-context';
import { NotOnListScreen } from '@/auth/NotOnListScreen';
import { SignInScreen } from '@/auth/SignInScreen';
import { ConfigMissingScreen } from '@/components/ConfigMissingScreen';
import { Splash } from '@/components/Splash';
import { createQueryClient } from '@/data/query-client';
import { useMemberForUser } from '@/data/members';
import { appEnv } from '@/lib/env';
import { routes } from '@/routes';

export function App() {
  const [queryClient] = useState(createQueryClient);
  if (!appEnv.ok) return <ConfigMissingScreen problems={appEnv.problems} />;
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthGate />
      </AuthProvider>
    </QueryClientProvider>
  );
}

function AuthGate() {
  const auth = useAuth();
  if (auth.status === 'loading') return <Splash />;
  if (auth.status === 'signed-out') {
    return (
      <SignInScreen onSignIn={() => void auth.signIn()} error={auth.error} waitingForBrowser={auth.waitingForBrowser} />
    );
  }
  return <MemberGate />;
}

function MemberGate() {
  const { session, signOut } = useAuth();
  const member = useMemberForUser(session?.user.id);
  const [router] = useState(() => createBrowserRouter(routes));

  if (member.isPending) return <Splash label="Checking the studio list" />;
  if (member.isError) return <Splash label={`Can't reach the studio: ${member.error.message}`} />;
  if (!member.data) {
    return (
      <NotOnListScreen
        email={session?.user.email}
        onSignOut={() => void signOut()}
        onCheckAgain={() => void member.refetch()}
        checking={member.isFetching}
      />
    );
  }
  return (
    <CurrentMemberContext value={member.data}>
      <RouterProvider router={router} />
    </CurrentMemberContext>
  );
}
